/**
 * Pronunciation Assessment Service
 * Manages pronunciation assessment interactions with the backend API
 */

import { logger } from '../utils/logger';
import { getApiUrl } from '../utils/apiUtils';

export interface PronunciationConfig {
  pronunciation_enable_prosody: boolean;
  pronunciation_enable_miscue: boolean;
  pronunciation_grading_system: 'FivePoint' | 'HundredMark';
  pronunciation_granularity: 'Phoneme' | 'Word' | 'FullText';
  pronunciation_dimension: 'Basic' | 'Comprehensive';
  pronunciation_accuracy_threshold: number;
  pronunciation_fluency_threshold: number;
  pronunciation_completeness_threshold: number;
  pronunciation_prosody_threshold: number;
  pronunciation_locale: string;
}

export interface PronunciationScores {
  accuracy: number;
  fluency: number;
  completeness: number;
  prosody: number;
  overall_pronunciation: number;
}

export interface WordDetail {
  word: string;
  accuracy_score: number;
  error_type: string;
  syllables: Array<{
    syllable: string;
    accuracy_score: number;
  }>;
  phonemes: Array<{
    phoneme: string;
    accuracy_score: number;
  }>;
}

export interface PronunciationAssessment {
  id: string;
  session_id: string;
  reference_text: string | null;
  recognized_text: string;
  scores: PronunciationScores;
  duration_seconds: number;
  locale: string;
  assessment_mode: 'scripted' | 'unscripted';
  created_at: string;
}

export interface SessionPronunciationAnalytics {
  session_id: string;
  has_assessment: boolean;
  assessment?: PronunciationAssessment;
  word_details?: WordDetail[];
  statistics?: {
    word_count: number;
    avg_word_accuracy: number;
    error_distribution: Record<string, number>;
    accuracy_distribution: {
      excellent: number;
      good: number;
      fair: number;
      needs_work: number;
    };
  };
  trends?: {
    accuracy: number[];
    fluency: number[];
    completeness: number[];
    prosody: number[];
  };
}

export interface PronunciationHistory {
  assessments: Array<{
    id: string;
    session_id: string;
    student_age: number;
    story_id: string | null;
    scores: PronunciationScores;
    duration_seconds: number;
    locale: string;
    assessment_mode: string;
    created_at: string;
    session_start: string;
  }>;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    pages: number;
  };
  aggregates: {
    avg_accuracy: number;
    avg_fluency: number;
    avg_completeness: number;
    avg_prosody: number;
    avg_overall: number;
  };
}

export interface PronunciationTrends {
  trends: Array<{
    date: string;
    accuracy: number;
    fluency: number;
    completeness: number;
    prosody: number;
    overall: number;
    assessments: number;
    unique_students: number;
  }>;
  period_summary: {
    total_assessments: number;
    avg_accuracy: number;
    avg_fluency: number;
    avg_completeness: number;
    avg_prosody: number;
    days_analyzed: number;
  };
}

export interface BufferStats {
  active_sessions: number;
  sessions: Record<string, {
    audio_chunk_count: number;
    total_audio_bytes: number;
    buffer_duration_seconds: number;
    is_recording: boolean;
  }>;
}

class PronunciationService {
  private baseUrl: string;

  constructor() {
    // @ts-ignore - process.env is available in React apps
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || '';
  }

  /**
   * Start pronunciation assessment for a conversation
   */
  async startAssessment(
    conversationId: string,
    referenceText?: string,
    studentAge?: number
  ): Promise<{ success: boolean; message: string }> {
    try {
      logger.logUIEvent('pronunciation_start_requested', {
        conversation_id: conversationId,
        has_reference_text: !!referenceText,
        assessment_mode: referenceText ? 'scripted' : 'unscripted'
      }, studentAge, conversationId);

      const response = await fetch(getApiUrl('/pronunciation/start'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          session_id: conversationId, // Use conversation_id as session_id
          user_age: studentAge || undefined,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start assessment');
      }

      const result = await response.json();

      logger.logUIEvent('pronunciation_started', {
        conversation_id: conversationId,
        success: result.success
      }, studentAge, conversationId);

      return result;

    } catch (error) {
      logger.logErrorEvent('pronunciation_start_failed', error as Error, {
        conversation_id: conversationId
      }, studentAge, conversationId);
      throw error;
    }
  }

  /**
   * Stop pronunciation assessment and get results
   */
  async stopAssessment(
    conversationId: string,
    referenceText?: string,
    studentAge?: number
  ): Promise<{ success: boolean; assessment_id: string; message: string }> {
    try {
      logger.logUIEvent('pronunciation_stop_requested', {
        conversation_id: conversationId
      }, studentAge, conversationId);

      const response = await fetch(getApiUrl('/pronunciation/stop'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          session_id: conversationId,
          reference_text: referenceText || undefined,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to stop assessment');
      }

      const result = await response.json();

      logger.logUIEvent('pronunciation_stopped', {
        conversation_id: conversationId,
        success: result.success,
        assessment_id: result.assessment_id
      }, studentAge, conversationId);

      return result;

    } catch (error) {
      logger.logErrorEvent('pronunciation_stop_failed', error as Error, {
        conversation_id: conversationId
      }, studentAge, conversationId);
      throw error;
    }
  }

  /**
   * Get pronunciation configuration
   */
  async getConfig(): Promise<PronunciationConfig> {
    try {
      const response = await fetch(getApiUrl('/pronunciation/config'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch configuration');
      }

      const result = await response.json();
      return result.config;

    } catch (error) {
      logger.logErrorEvent('pronunciation_config_fetch_failed', error as Error);
      throw error;
    }
  }

  /**
   * Update pronunciation configuration
   */
  async updateConfig(config: Partial<PronunciationConfig>): Promise<{ success: boolean; message: string }> {
    try {
      logger.logUIEvent('pronunciation_config_update_requested', {
        updated_fields: Object.keys(config).length
      });

      const response = await fetch(getApiUrl('/pronunciation/config'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ config }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update configuration');
      }

      const result = await response.json();

      logger.logUIEvent('pronunciation_config_updated', {
        success: result.success
      });

      return result;

    } catch (error) {
      logger.logErrorEvent('pronunciation_config_update_failed', error as Error);
      throw error;
    }
  }

  /**
   * Get assessment results for a specific session
   */
  async getResults(sessionId: string, studentAge?: number): Promise<PronunciationAssessment | null> {
    try {
      const response = await fetch(getApiUrl(`/pronunciation/results/${sessionId}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // No assessment found
        }
        throw new Error('Failed to fetch results');
      }

      const result = await response.json();

      logger.logUIEvent('pronunciation_results_fetched', {
        session_id: sessionId,
        has_assessment: !!result.assessment
      }, studentAge, sessionId);

      return result.assessment;

    } catch (error) {
      logger.logErrorEvent('pronunciation_results_fetch_failed', error as Error, {
        session_id: sessionId
      }, studentAge, sessionId);
      throw error;
    }
  }

  /**
   * Get comprehensive pronunciation analytics for a session
   * (Used by Teacher Dashboard)
   * @param sessionId - The session/conversation ID
   * @param storySessionId - Optional story session ID to filter for specific story
   */
  async getSessionAnalytics(sessionId: string, storySessionId?: string | null): Promise<SessionPronunciationAnalytics> {
    try {
      // Build URL with optional story_session_id query parameter
      let url = getApiUrl(`/v1/dashboard/session/${sessionId}/pronunciation`);
      if (storySessionId) {
        url += `?story_session_id=${encodeURIComponent(storySessionId)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch session analytics');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      logger.logErrorEvent('pronunciation_analytics_fetch_failed', error as Error, {
        session_id: sessionId,
        story_session_id: storySessionId
      });
      throw error;
    }
  }

  /**
   * Get pronunciation history across sessions
   * (Used by Teacher Dashboard)
   */
  async getHistory(
    studentAge?: number,
    limit: number = 20,
    offset: number = 0
  ): Promise<PronunciationHistory> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      if (studentAge) {
        params.append('student_age', studentAge.toString());
      }

      const response = await fetch(getApiUrl(`/dashboard/pronunciation/history?${params}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pronunciation history');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      logger.logErrorEvent('pronunciation_history_fetch_failed', error as Error, {
        student_age: studentAge
      });
      throw error;
    }
  }

  /**
   * Get pronunciation trends over time
   * (Used by Teacher Dashboard)
   */
  async getTrends(days: number = 30): Promise<PronunciationTrends> {
    try {
      const response = await fetch(getApiUrl(`/dashboard/analytics/pronunciation-trends?days=${days}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pronunciation trends');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      logger.logErrorEvent('pronunciation_trends_fetch_failed', error as Error, {
        days
      });
      throw error;
    }
  }

  /**
   * Get pronunciation configuration from dashboard
   * (Used by Teacher Dashboard)
   */
  async getDashboardConfig(): Promise<Record<string, any>> {
    try {
      const response = await fetch(getApiUrl('/dashboard/pronunciation/config'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard pronunciation config');
      }

      const result = await response.json();
      return result.pronunciation_config;

    } catch (error) {
      logger.logErrorEvent('pronunciation_dashboard_config_fetch_failed', error as Error);
      throw error;
    }
  }

  /**
   * Get buffer statistics (for debugging/monitoring)
   */
  async getBufferStats(): Promise<BufferStats> {
    try {
      const response = await fetch(getApiUrl('/pronunciation/buffer/stats'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch buffer stats');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      logger.logErrorEvent('pronunciation_buffer_stats_fetch_failed', error as Error);
      throw error;
    }
  }

  /**
   * Health check for pronunciation service
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await fetch(getApiUrl('/pronunciation/health'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Health check failed');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      logger.logErrorEvent('pronunciation_health_check_failed', error as Error);
      throw error;
    }
  }

  /**
   * Get aggregated pronunciation results for a session
   * Combines multiple segment assessments into overall metrics
   */
  async getSessionAggregate(sessionId: string): Promise<SessionAggregateResult> {
    try {
      logger.logVoiceLiveEvent('pronunciation_aggregate_requested', {
        session_id: sessionId
      });

      const response = await fetch(getApiUrl(`/pronunciation/session/${sessionId}/aggregate`));
      
      if (!response.ok) {
        throw new Error(`Failed to get session aggregate: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        logger.logVoiceLiveEvent('pronunciation_aggregate_received', {
          session_id: sessionId,
          total_segments: result.overall_metrics?.total_segments,
          total_words: result.overall_metrics?.total_words_assessed,
          overall_accuracy: result.overall_metrics?.accuracy
        });
      }
      
      return result;

    } catch (error) {
      logger.logErrorEvent('pronunciation_aggregate_failed', error as Error, {
        session_id: sessionId
      });
      throw error;
    }
  }
}

// Types for session aggregation
export interface SessionAggregateResult {
  success: boolean;
  session_id: string;
  overall_metrics?: {
    accuracy: number;
    fluency: number;
    pronunciation: number;
    total_words_assessed: number;
    total_segments: number;
  };
  progress?: {
    improvement: number | null;
    first_segment_accuracy: number | null;
    last_segment_accuracy: number | null;
  };
  mispronunciations?: {
    count: number;
    words: Array<{
      word: string;
      accuracy: number;
      error_type: string;
      segment: number;
    }>;
  };
  segments?: Array<{
    segment: number;
    assessment_id: number;
    accuracy: number;
    fluency: number;
    pronunciation: number;
    word_count: number;
    error_count: number;
    timestamp: string;
  }>;
  error?: string;
}

// Export singleton instance
export const pronunciationService = new PronunciationService();
export default pronunciationService;
